/**
 * First-match readiness on `/play` (QA batch 3 #1): what the next match will be (deck, rules tier,
 * automation, clock, opponent) and what still stops it, each problem with its fix. Pure helpers; the
 * card is `ReadinessCard.tsx`. The hub's choices (tier, opponent, bot deck and level) are kept on this
 * device so they survive a trip to runebound.gg to fix a deck (the deck itself: `rememberLastDeck`).
 */
import { TIMING } from '@play/protocol';
import type { EnabledTier } from '../../lib/env';
import type { PlaySettings } from '../../settings/schema';

/** Who the next match is against. */
export type OpponentKind = 'human' | 'bot';

/** The hub's choices kept on this device. */
export interface HubChoices {
  tier: EnabledTier | null;
  opponent: OpponentKind;
  botDeck: string;
  botLevel: 'easy' | 'normal';
}

const CHOICES_KEY = 'runebound.play.hubChoices';

/** Defaults: the profile's tier, a human opponent, the bot on Normal with a mirror deck. */
export const DEFAULT_CHOICES: HubChoices = { tier: null, opponent: 'human', botDeck: 'mirror', botLevel: 'normal' };

/** Parses stored choices, field by field (anything broken falls back to the default). */
export function parseHubChoices(raw: unknown): HubChoices {
  const r = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  return {
    tier: r.tier === 1 || r.tier === 2 ? r.tier : null,
    opponent: r.opponent === 'bot' ? 'bot' : 'human',
    botDeck: typeof r.botDeck === 'string' && r.botDeck.length > 0 && r.botDeck.length < 128 ? r.botDeck : 'mirror',
    botLevel: r.botLevel === 'easy' ? 'easy' : 'normal',
  };
}

/** The choices stored on this device (defaults when none or unreadable). */
export function readHubChoices(): HubChoices {
  try {
    const raw = localStorage.getItem(CHOICES_KEY);
    return raw ? parseHubChoices(JSON.parse(raw)) : DEFAULT_CHOICES;
  } catch {
    return DEFAULT_CHOICES;
  }
}

/** Stores the choices on this device (best effort). */
export function saveHubChoices(choices: HubChoices): void {
  try {
    localStorage.setItem(CHOICES_KEY, JSON.stringify(choices));
  } catch {
    // Not persisted: fine.
  }
}

/** One line on what a tier means, tier 1 against tier 2 (docs/design/02 §2.1, owner decision Q5). */
export const TIER_LINE: Record<EnabledTier, string> = {
  1: 'A shared playmat. Nothing is automated; you and your opponent run the rules by hand.',
  2: 'The playmat plus a rules advisor that highlights legal plays and does the bookkeeping you turn on.',
};

const AUTOMATION_LABELS: [keyof PlaySettings['automation'], string][] = [
  ['autoReady', 'ready'],
  ['autoDraw', 'draw'],
  ['autoChannel', 'channel'],
  ['autoHeal', 'heal'],
];

/** The automation the next match will use, in one line (tier 1 has none). */
export function automationSummary(tier: EnabledTier, settings: PlaySettings): string {
  if (tier === 1) return 'None in tier 1: every step is done by hand.';
  const a = settings.automation;
  const on = AUTOMATION_LABELS.filter(([k]) => a[k]).map(([, label]) => label);
  const parts = [
    on.length === 0 ? 'No automatic steps' : `Automatic ${on.join(', ')}`,
    a.triggerReminders ? 'trigger reminders on' : 'trigger reminders off',
    settings.autoPass ? 'auto-pass on' : 'auto-pass off',
  ];
  return `${parts.join(' · ')}.`;
}

/** The clock (owner decision Q26), from the protocol's timing constants. */
export function clockSummary(): string {
  const bank = Math.round(TIMING.bankMs / 60_000);
  const decision = Math.round(TIMING.decisionMs / 1000);
  return `${bank} min chess clock each, ${decision} s per decision.`;
}

/** The opponent in words. */
export function opponentSummary(choices: Pick<HubChoices, 'opponent' | 'botLevel'>, botDeckName: string): string {
  if (choices.opponent === 'human') return 'A person: the casual queue, a private room or a friend.';
  return `Practice bot, ${choices.botLevel === 'easy' ? 'Easy' : 'Normal'}, playing ${botDeckName}.`;
}

/** The selected deck's check, as the readiness card needs it. */
export type DeckState =
  | { s: 'none' }
  | { s: 'loading' }
  | { s: 'error'; message: string }
  | { s: 'checked'; legal: boolean; issues: number; playable: boolean; manual: number };

/** Something that stops (`bad`) or qualifies (`info`) the next match, with its fix. */
export interface ReadinessFlag {
  id: 'noDecks' | 'noDeck' | 'deckError' | 'illegal' | 'tier' | 'manual' | 'botDeck';
  tone: 'bad' | 'info';
  text: string;
  fix: 'build' | 'editDeck' | 'recheck' | 'pickDeck' | 'pickBotDeck' | null;
}

/** Inputs to the readiness check. */
export interface ReadinessInput {
  /** Number of decks, `null` while loading. */
  decks: number | null;
  deck: DeckState;
  tier: EnabledTier;
  opponent: OpponentKind;
  /** The bot deck chosen and the presets offered (`null` while loading). */
  botDeck: string;
  presets: readonly string[] | null;
}

/**
 * What stops or qualifies the next match. `bad` flags disable the start buttons; the card shows every
 * flag inline with its fix (build or edit the deck on runebound.gg, check again, pick another one).
 */
export function readinessFlags(input: ReadinessInput): ReadinessFlag[] {
  const out: ReadinessFlag[] = [];
  if (input.decks === 0) {
    out.push({ id: 'noDecks', tone: 'bad', text: 'You have no decks yet.', fix: 'build' });
    return out;
  }
  const d = input.deck;
  if (d.s === 'none' && input.decks !== null) {
    out.push({ id: 'noDeck', tone: 'bad', text: 'No deck selected.', fix: 'pickDeck' });
  } else if (d.s === 'error') {
    out.push({ id: 'deckError', tone: 'bad', text: `Couldn’t check this deck: ${d.message}`, fix: 'recheck' });
  } else if (d.s === 'checked') {
    if (!d.legal) {
      out.push({
        id: 'illegal',
        tone: 'bad',
        text: `This deck isn’t legal in Standard (${d.issues} ${d.issues === 1 ? 'issue' : 'issues'}).`,
        fix: 'editDeck',
      });
    } else if (!d.playable) {
      out.push({ id: 'tier', tone: 'bad', text: `This deck can’t be played in tier ${input.tier}.`, fix: 'pickDeck' });
    } else if (input.tier === 2 && d.manual > 0) {
      out.push({
        id: 'manual',
        tone: 'info',
        text: `${d.manual} ${d.manual === 1 ? 'card isn’t' : 'cards aren’t'} automated yet; you play ${d.manual === 1 ? 'it' : 'them'} by hand.`,
        fix: null,
      });
    }
  }
  if (input.opponent === 'bot' && input.botDeck !== 'mirror' && input.presets !== null) {
    if (!input.presets.includes(input.botDeck)) {
      out.push({
        id: 'botDeck',
        tone: 'bad',
        text: 'The bot deck you chose is no longer offered.',
        fix: 'pickBotDeck',
      });
    }
  }
  return out;
}

/** Whether a match can start: a checked, playable deck and no blocking flag. */
export function readyToStart(input: ReadinessInput, flags: readonly ReadinessFlag[]): boolean {
  return input.deck.s === 'checked' && input.deck.playable && !flags.some((f) => f.tone === 'bad');
}
